import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  ValidationContext,
  ValidationType,
} from '~/components/hoc/withValidation';
import Button, { ButtonType } from '~/components/shared/Button/button';
import Card from '~/components/shared/Card/Card';
import CardHeader from '~/components/shared/CardHeader/CardHeader';
import CardSection from '~/components/shared/CardSection/CardSection';
import InputField from '~/components/shared/Input/InputField';
import { Typography } from '~/components/shared/Typography/Typography';
import { CountryCode, OwnerTypeEnum } from '~/enums';
import useTranslate from '~/hooks/useTranslate';
import { LoanOwner, Offer } from '~/Interfaces';
import * as offerActions from '~/store/actions/loanOfferActions';
import { RootState } from '~/store/types/sharedTypes';
import { theme } from '~/styles/themes';
import { AdditionalOwner } from '~/pages/LoanOffer/components/Ownership/AdditionalOwner';
import { formatNameHelper } from '~/helpers/formatName.helper';
import Select from '~/components/shared/Select/Select';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';

interface Props {
  data: Offer;
}

export type OwnerType =
  | 'OWN_MORE_THAN_75'
  | 'MORE_PERSONS_OWN_25'
  | 'NON_ONE_OWN_25';

const NewPersonErrorContainer = styled.div`
  margin-top: 0.5em;
`;

const CompanyOwnersCard: React.FC<Props> = (props) => {
  /* States */
  const [newPersonIsAllowed, setNewPersonIsAllowed] = useState(true);
  const [
    addAnotherPersonButtonHidden,
    setAddAnotherPersonButtonHidden,
  ] = useState(true);
  const [ownerType, setOwnerType] = useState('');

  /* Selectors */

  const { owners: beneficialOwners, yourOwnShares } = useSelector(
    (state: RootState) => state.loanOffer.userData
  );
  const { market, language } = useSelector((state: RootState) => state.intl);

  /* Other hooks */
  const dispatch = useDispatch();
  const t = useTranslate();
  const validationContext = useContext(ValidationContext);

  const ownersTotalShares = useMemo(
    () =>
      beneficialOwners
        .map((o) => Number(o.percentage))
        .reduce((sum, current) => sum + current, 0),
    [JSON.stringify(beneficialOwners)]
  );

  /* Effects */
  useEffect(() => {
    dispatch(
      offerActions.updateOfferOwner({
        name: props.data.clientName,
        personalNumber:
          market === CountryCode.NL
            ? props.data.dateOfBirth
            : props.data.personalNumber,
        percentage: yourOwnShares,
        id: '01',
      })
    );
  }, [yourOwnShares]);

  const ownerTypeOptions = [
    {
      label: t('CompanyOwnersCard.OwnerType1'),
      value: OwnerTypeEnum.MoreThan75,
    },
    {
      label: t('CompanyOwnersCard.OwnerType2'),
      value: OwnerTypeEnum.MorePersonsOwn25,
    },
    {
      label: t('CompanyOwnersCard.OwnerType3'),
      value: OwnerTypeEnum.NonOneOwn25,
    },
  ];

  const yourOwnSharesHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(offerActions.updateOfferYourOwnShares(event.currentTarget.value));
  };

  const yourOwnSharesOnBlurHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event?.currentTarget?.value;
    if (value)
      dispatch(offerActions.updateOfferYourOwnShares(value.replace(/\%/g, '')));
  };

  const addOwnerPerson = () => {
    const areAllPersonsFilled = beneficialOwners.every(
      (p) => p.id && p.name && p.personalNumber && p.percentage
    );
    if (areAllPersonsFilled) {
      setNewPersonIsAllowed(true);
      dispatch(offerActions.addEmptyOwnerPlaceholder());
    } else {
      setNewPersonIsAllowed(false);
    }
  };

  const removeOwner = (id: string) => {
    if (beneficialOwners.length > 0) {
      dispatch(offerActions.removeOwnerPlaceholder(id));
    }
    setNewPersonIsAllowed(true);
  };

  const onOwnerTypeChange = (value: OwnerTypeEnum) => {
    setOwnerType(value);
    validationContext.removePropertyFromValidationErrors(
      'TotalOwnershipExceeded'
    );
    validationContext.removePropertyFromValidationErrors(
      'TotalOwnershipNotReached'
    );

    // upon change of the ownerType value, we want "yourOwnShares" input field to get revalidated, hence the following:
    validationContext.onChangeHOC({
      target: {
        name: 'yourOwnShares',
        value: yourOwnShares,
        dataset: {
          validationType: `${'PercentageShare'}_${value}` as ValidationType,
        },
      },
    });

    if (beneficialOwners.length > 1) {
      for (let index = 1; index < beneficialOwners.length; index += 1) {
        removeOwner(beneficialOwners[index].id);
      }
    }
    dispatch(
      offerActions.updateOfferData({
        ownerType: value,
      })
    );
  };

  const getOwnerPlaceholderText = () => {
    if (market === 'NL' && language === 'NL' && props.data.clientName) {
      return t('CompanyOwnersCard.YourOwnSharesPlaceHolder', {
        clientName: formatNameHelper(props.data.clientName),
      }) as string;
    }
    return t('CompanyOwnersCard.YourOwnSharesPlaceHolder') as string;
  };

  useEffect(() => {
    if (
      ownerType === OwnerTypeEnum.MorePersonsOwn25 ||
      ownerType === OwnerTypeEnum.NonOneOwn25
    ) {
      setAddAnotherPersonButtonHidden(ownersTotalShares >= 100);
    }

    return () => {
      setAddAnotherPersonButtonHidden(true);
    };
  }, [ownerType, ownersTotalShares]);

  useEffect(() => {
    if (ownersTotalShares > 100) {
      validationContext.addPropertyToValidationErrors('TotalOwnershipExceeded');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'TotalOwnershipExceeded'
      );
    };
  }, [ownersTotalShares]);

  return (
    <Card noPadding dataTestId="loan-offer-company-owners-card">
      <CardHeader value={t('CompanyOwnersCard.Title') as string} />
      <CardSection borderBottom borderTop>
        <Select
          name="ownerType"
          validationType="OwnerType"
          placeholder={`- ${t('OwnerType')} - `}
          value={ownerTypeOptions.filter((o) => o.value === ownerType)[0]}
          onChange={(newValue) =>
            onOwnerTypeChange(newValue?.value ?? ('' as OwnerTypeEnum))
          }
          options={ownerTypeOptions}
        />
        <InputField
          name="yourOwnShares"
          value={yourOwnShares}
          validationType={`${'PercentageShare'}_${ownerType}` as ValidationType}
          onChange={(event) => yourOwnSharesHandler(event)}
          onBlur={(event) => yourOwnSharesOnBlurHandler(event)}
          placeholder={getOwnerPlaceholderText()}
        />
      </CardSection>
      {beneficialOwners.slice(1).map((owner: LoanOwner, index: number) => (
        <AdditionalOwner
          key={index}
          owner={owner}
          index={index}
          setNewPersonIsAllowed={setNewPersonIsAllowed}
        />
      ))}
      {!addAnotherPersonButtonHidden && (
        <CardSection>
          <Button type={ButtonType.Outlined} onClick={addOwnerPerson}>
            {t('CompanyOwnersCard.AddAnotherPerson') as string}
          </Button>

          {!newPersonIsAllowed && (
            <NewPersonErrorContainer>
              <ValidationErrorMessage>
                {t('CompanyOwnersCard.NewOwnerError')}
              </ValidationErrorMessage>
            </NewPersonErrorContainer>
          )}
        </CardSection>
      )}
      {!Number.isNaN(ownersTotalShares) &&
        ownerType === OwnerTypeEnum.MorePersonsOwn25 &&
        !beneficialOwners
          .map((o) => o.percentage)
          .some((x) => Number(x) >= 25) && (
          <CardSection>
            <ValidationErrorMessage>
              {t('ValidationErrors.AtLeastOneOwnerMoreThan25')}
            </ValidationErrorMessage>
          </CardSection>
        )}
      {!Number.isNaN(ownersTotalShares) &&
        (ownerType === OwnerTypeEnum.MorePersonsOwn25 ||
          ownerType === OwnerTypeEnum.NonOneOwn25) && (
          <CardSection>
            {ownersTotalShares > 100 ? (
              <ValidationErrorMessage>
                {t('CompanyOwnersCard.TotalOwnershipSharesMoreThan100')}
              </ValidationErrorMessage>
            ) : (
              <Typography color={theme.colors.neutral} fontSize={0.8}>
                {t('CompanyOwnersCard.TotalOwnershipShares')}{' '}
                {ownersTotalShares.toFixed(2)} %
              </Typography>
            )}
          </CardSection>
        )}
    </Card>
  );
};

export default CompanyOwnersCard;
